/**
 * 邀请码页面
 * 用户可以查看自己的邀请码、邀请记录和统计数据
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { SiteHeader } from '@/components/SiteHeder';
import { Gift, Users, Coins, Copy, Check, Loader2, Calendar, User, ChevronLeft, ChevronRight, Download, Smartphone } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { copyTextToClipboard } from '@/lib/clipboard';
import { apiClient } from '@/services/api';
import type { InvitationPageConfig } from '@xiaozhi/shared-types';

interface InvitationStats {
  total: number;
  completed: number;
  totalRewardQuota: number;
}

interface InvitationInfo {
  invitationCode: string;
  stats: InvitationStats;
  maxInvitations: number;
  rewardQuota: number;
}

interface InvitationRecord {
  id: string;
  invitee_username: string;
  reward_quota: number;
  status: string;
  created_at: string;
}

interface AppVersionInfo {
  hasUpdate: boolean;
  versionName?: string;
  versionCode?: number;
  updateType?: string;
  downloadUrl?: string;
  description?: string;
  fileMd5?: string;
  fileSize?: number;
  minVersionCode?: number;
}

/** 兜底下载链接：无法从后端取到最新版时使用 */
const FALLBACK_DOWNLOAD_URL = '/api/app-version/download/1776334087899_FeyaGate-v1.0.22-release.apk';

export function InvitationPage() {
  const [loading, setLoading] = useState(true);
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null);
  const [invitationList, setInvitationList] = useState<InvitationRecord[]>([]);
  const [copied, setCopied] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Poster config state
  const [posterConfig, setPosterConfig] = useState<InvitationPageConfig | null>(null);
  const [currentPosterIndex, setCurrentPosterIndex] = useState(0);

  // App version state
  const [appVersion, setAppVersion] = useState<AppVersionInfo | null>(null);
  const [appVersionLoading, setAppVersionLoading] = useState(false);
  // 最新版 App 下载地址（动态获取，避免硬编码旧版 APK）
  const [latestDownloadUrl, setLatestDownloadUrl] = useState<string | undefined>(undefined);

  // 获取邀请信息（兼容后端 snake_case 格式）
  const loadInvitationInfo = useCallback(async () => {
    try {
      setLoading(true);
      const token = useAuthStore.getState().accessToken;
      const response = await fetch('/api/user/invitation', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success && data.data) {
        const raw = data.data as Record<string, unknown>;
        setInvitationInfo({
          invitationCode: (raw.invitation_code as string) || '',
          stats: {
            total: (raw.invitation_count as number) ?? 0,
            completed: (raw.invitation_count as number) ?? 0,
            totalRewardQuota: 0,
          },
          maxInvitations: (raw.max_invitations as number) ?? 20,
          rewardQuota: (raw.reward_quota as number) ?? 200000,
        });
      }
    } catch (error) {
      console.error('获取邀请信息失败:', error);
      toast.error('获取邀请信息失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 加载海报配置
  const loadPosterConfig = useCallback(async () => {
    try {
      const config = await apiClient.getConfig();
      setPosterConfig(config?.invitationPage || null);
    } catch (error) {
      console.error('加载海报配置失败:', error);
    }
  }, []);

  // 获取邀请列表（兼容后端 data.data + pagination 嵌套格式及 snake_case）
  const loadInvitationList = useCallback(async (page: number) => {
    try {
      setListLoading(true);
      const token = useAuthStore.getState().accessToken;
      const response = await fetch(`/api/user/invitation/list?page=${page}&page_size=10`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success && data.data) {
        const inner = data.data as { data?: InvitationRecord[]; pagination?: Record<string, number> };
        const items = Array.isArray(inner.data) ? inner.data : [];
        const pag = inner.pagination ?? {};
        setInvitationList(items);
        setTotalPages(pag.total_pages ?? pag.totalPages ?? 1);
        setCurrentPage(pag.page ?? 1);
      }
    } catch (error) {
      console.error('获取邀请列表失败:', error);
      toast.error('获取邀请列表失败');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvitationInfo();
    loadInvitationList(1);
    loadPosterConfig();
    // 检查 App 版本更新
    checkAppVersion();
  }, [loadInvitationInfo, loadInvitationList, loadPosterConfig]);

  // 检查 App 版本更新
  const checkAppVersion = useCallback(async () => {
    try {
      setAppVersionLoading(true);
      const versionInfo = await apiClient.checkAppVersion();
      setAppVersion(versionInfo);
      // 同时记录最新版下载地址（current_version_code=0 时返回的即最新版）
      if (versionInfo?.downloadUrl) setLatestDownloadUrl(versionInfo.downloadUrl);
    } catch (error) {
      console.error('检查 App 版本失败:', error);
    } finally {
      setAppVersionLoading(false);
    }
  }, []);

  // 轮播自动播放
  useEffect(() => {
    if (!posterConfig?.enabled || posterConfig.layout !== 'carousel' || !posterConfig.autoPlay) {
      return;
    }

    const enabledPosters = posterConfig.posterImages?.filter((p) => p.enabled) || [];
    if (enabledPosters.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentPosterIndex((prev) => (prev + 1) % enabledPosters.length);
    }, posterConfig.interval || 3000);

    return () => clearInterval(interval);
  }, [posterConfig]);

  // 复制邀请码
  const handleCopyCode = async () => {
    if (!invitationInfo?.invitationCode) return;

    const registerUrl = `${window.location.origin}/register?invitationCode=${invitationInfo.invitationCode}`;

    try {
      await navigator.clipboard.writeText(registerUrl);
      setCopied(true);
      toast.success('邀请链接已复制到剪贴板');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // 降级方案：使用传统方法
      const textArea = document.createElement('textarea');
      textArea.value = registerUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        toast.success('邀请链接已复制到剪贴板');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error('复制失败，请手动复制邀请码');
      }
      document.body.removeChild(textArea);
    }
  };

  // 复制邀请码（仅代码）
  const handleCopyCodeOnly = async () => {
    if (!invitationInfo?.invitationCode) return;

    const ok = await copyTextToClipboard(invitationInfo.invitationCode);
    if (ok) {
      toast.success('邀请码已复制到剪贴板');
    } else {
      toast.error('复制失败，请手动复制');
    }
  };

  // 格式化日期
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 格式化 token 数量
  const formatQuota = (quota: number) => {
    if (quota >= 10000) {
      return `${(quota / 10000).toFixed(1)}万`;
    }
    return quota.toString();
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <SiteHeader title="邀请好友" />
          <div className="flex flex-1 flex-col gap-4 p-4 pt-6">
            <div className="flex items-center justify-center min-h-[400px]">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <SiteHeader title="邀请好友" />
        <div className="flex flex-1 flex-col gap-4 p-4 pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">邀请好友</h1>
              <p className="text-muted-foreground mt-1">
                邀请好友注册，双方各获得 {formatQuota(invitationInfo?.rewardQuota || 200000)} token 奖励
              </p>
            </div>
          </div>

          {/* 海报展示区 */}
          {posterConfig?.posterImages && posterConfig.posterImages.length > 0 && (() => {
            const enabledPosters = posterConfig.posterImages.filter((p) => p.enabled);
            if (enabledPosters.length === 0) return null;

            // 渲染轮播模式
            if (posterConfig.layout === 'carousel') {
              const currentPoster = enabledPosters[currentPosterIndex];
              return (
                <Card className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="relative aspect-[3/2] sm:aspect-video sm:max-h-[400px] bg-muted poster-saveable">
                      <img
                        src={currentPoster.url}
                        alt={currentPoster.title || '海报'}
                        className="w-full h-full object-contain sm:object-contain"
                      />
                      {/* 标题覆盖层 - pointer-events-none 让触摸穿透到图片 */}
                      {currentPoster.title && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 sm:p-4 pointer-events-none">
                          <p className="text-white font-medium text-sm sm:text-base">{currentPoster.title}</p>
                          {currentPoster.description && (
                            <p className="text-white/80 text-xs sm:text-sm">{currentPoster.description}</p>
                          )}
                        </div>
                      )}
                      {/* 引导提示 */}
                      <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-black/40 backdrop-blur-sm text-white text-xs px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full flex items-center gap-1 pointer-events-none">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        <span className="hidden sm:inline">长按图片可保存到相册</span>
                        <span className="sm:hidden">长按保存</span>
                      </div>
                      {/* 链接覆盖层 - 仅在桌面端可点击 */}
                      {currentPoster.link && (
                        <a href={currentPoster.link} target="_blank" rel="noopener noreferrer" className="absolute inset-0 hidden sm:block" />
                      )}
                    </div>
                    {enabledPosters.length > 1 && (
                      <div className="flex items-center justify-center gap-2 p-2 bg-background">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setCurrentPosterIndex((prev) => (prev === 0 ? enabledPosters.length - 1 : prev - 1))}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <div className="flex gap-1">
                          {enabledPosters.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentPosterIndex(index)}
                              className={`w-2 h-2 rounded-full transition-colors ${
                                index === currentPosterIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                              }`}
                            />
                          ))}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setCurrentPosterIndex((prev) => (prev + 1) % enabledPosters.length)}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            }

            // 渲染网格模式
            if (posterConfig.layout === 'grid') {
              return (
                <div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                    {enabledPosters.map((poster, index) => (
                      <Card key={poster.url} className="overflow-hidden">
                        <CardContent className="p-0">
                          <div className="relative aspect-square bg-muted poster-saveable">
                            <img
                              src={poster.url}
                              alt={poster.title || `海报 ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            {poster.title && (
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 pointer-events-none">
                                <p className="text-white text-xs sm:text-sm font-medium truncate">{poster.title}</p>
                              </div>
                            )}
                            {poster.link && (
                              <a href={poster.link} target="_blank" rel="noopener noreferrer" className="absolute inset-0 hidden sm:block" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  {/* 网格模式引导 */}
                  <p className="text-center text-xs sm:text-sm text-muted-foreground mt-3 flex items-center justify-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    长按图片可保存到相册，分享给更多好友
                  </p>
                </div>
              );
            }

            // 渲染单图模式
            if (posterConfig.layout === 'single') {
              const poster = enabledPosters[0];
              return (
                <Card className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="relative aspect-[3/2] sm:aspect-video sm:max-h-[400px] bg-muted poster-saveable">
                      <img
                        src={poster.url}
                        alt={poster.title || '海报'}
                        className="w-full h-full object-contain"
                      />
                      {poster.title && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 sm:p-4 pointer-events-none">
                          <p className="text-white font-medium text-sm sm:text-base">{poster.title}</p>
                          {poster.description && (
                            <p className="text-white/80 text-xs sm:text-sm">{poster.description}</p>
                          )}
                        </div>
                      )}
                      {/* 引导提示 */}
                      <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-black/40 backdrop-blur-sm text-white text-xs px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full flex items-center gap-1 pointer-events-none">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        <span className="hidden sm:inline">长按图片可保存到相册</span>
                        <span className="sm:hidden">长按保存</span>
                      </div>
                      {/* 链接覆盖层 - 仅桌面端 */}
                      {poster.link && (
                        <a href={poster.link} target="_blank" rel="noopener noreferrer" className="absolute inset-0 hidden sm:block" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            }

            return null;
          })()}

          <div className="grid gap-4 md:grid-cols-3">
            {/* 统计卡片 */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">成功邀请</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{invitationInfo?.stats.total || 0}</div>
                <p className="text-xs text-muted-foreground">
                  已邀请 {invitationInfo?.stats.completed || 0} 人完成注册
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">累计奖励</CardTitle>
                <Coins className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatQuota(invitationInfo?.stats.totalRewardQuota || 0)}
                </div>
                <p className="text-xs text-muted-foreground">token</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">邀请额度</CardTitle>
                <Gift className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {invitationInfo?.stats.total || 0} / {invitationInfo?.maxInvitations || 20}
                </div>
                <p className="text-xs text-muted-foreground">
                  还可邀请 {(invitationInfo?.maxInvitations || 20) - (invitationInfo?.stats.total || 0)} 人
                </p>
              </CardContent>
            </Card>
          </div>

          {/* 邀请码卡片 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-orange-500" />
                我的邀请码
              </CardTitle>
              <CardDescription>
                分享邀请链接给好友，注册后双方均可获得奖励
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="text-sm text-muted-foreground mb-1">邀请码</div>
                  <div className="flex items-center gap-2">
                    <div className="font-mono text-xl font-bold bg-muted px-4 py-2 rounded-md">
                      {invitationInfo?.invitationCode || '加载中...'}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopyCodeOnly}
                      title="复制邀请码"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="text-sm text-muted-foreground mb-1">邀请链接</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-muted px-4 py-2 rounded-md text-sm truncate">
                      {invitationInfo?.invitationCode
                        ? `${window.location.origin}/register?invitationCode=${invitationInfo.invitationCode}`
                        : '加载中...'}
                    </div>
                    <Button onClick={handleCopyCode} className="whitespace-nowrap">
                      {copied ? (
                        <>
                          <Check className="mr-2 h-4 w-4" />
                          已复制
                        </>
                      ) : (
                        <>
                          <Copy className="mr-2 h-4 w-4" />
                          复制链接
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <Alert>
                <Gift className="h-4 w-4" />
                <AlertDescription>
                  好友使用您的邀请码注册后，双方各获得 <strong>{formatQuota(invitationInfo?.rewardQuota || 200000)} token</strong> 奖励！
                  每个用户最多可邀请 <strong>{invitationInfo?.maxInvitations || 20}</strong> 人。
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* App 下载卡片 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-blue-600" />
                App 下载
              </CardTitle>
              <CardDescription>
                下载 Alita Planet App，随时随地畅享 AI 对话
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {appVersionLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">检查版本中...</span>
                </div>
              ) : appVersion?.hasUpdate ? (
                <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      发现新版本：v{appVersion.versionName} (build {appVersion.versionCode})
                    </span>
                    <Badge variant="default" className="ml-auto">
                      {appVersion.updateType === 'force' ? '强制更新' : '可选更新'}
                    </Badge>
                  </div>
                  {appVersion.description && (
                    <p className="text-sm text-muted-foreground">{appVersion.description}</p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {appVersion.fileSize && (
                      <span>大小：{(appVersion.fileSize / 1024 / 1024).toFixed(1)} MB</span>
                    )}
                    {appVersion.fileMd5 && (
                      <span>MD5：{appVersion.fileMd5.substring(0, 8)}...</span>
                    )}
                  </div>
                  <Button
                    onClick={() => {
                      if (appVersion.downloadUrl) {
                        window.open(appVersion.downloadUrl, '_blank');
                      }
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={!appVersion?.downloadUrl}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    立即下载
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Smartphone className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground">当前已是最新版本</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    扫码或点击下方按钮下载最新版
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4 w-full"
                    onClick={() => window.open(latestDownloadUrl || FALLBACK_DOWNLOAD_URL, '_blank')}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    下载最新版 APK
                  </Button>
                </div>
              )}

              <div className="pt-4 border-t">
                <p className="text-center text-sm text-muted-foreground mb-3">
                  或扫描二维码下载
                </p>
                <div className="flex justify-center">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.origin + (latestDownloadUrl || FALLBACK_DOWNLOAD_URL))}`}
                    alt="下载二维码"
                    className="w-32 h-32"
                  />
                </div>
                <p className="text-center text-xs text-muted-foreground mt-2">
                  扫码下载最新版 Alita Planet App
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 邀请记录 */}
          <Card>
            <CardHeader>
              <CardTitle>邀请记录</CardTitle>
              <CardDescription>查看您邀请的好友列表</CardDescription>
            </CardHeader>
            <CardContent>
              {listLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : invitationList.length > 0 ? (
                <div className="space-y-4">
                  {invitationList.map((record) => (
                    <div
                      key={record.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">{record.invitee_username}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            <Calendar className="h-3 w-3" />
                            {formatDate(record.created_at)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={record.status === 'completed' ? 'default' : 'secondary'}
                        >
                          {record.status === 'completed' ? '已完成' : '处理中'}
                        </Badge>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <Coins className="h-3 w-3" />
                          +{formatQuota(record.reward_quota)}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* 分页 */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 pt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => loadInvitationList(currentPage - 1)}
                      >
                        上一页
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        第 {currentPage} / {totalPages} 页
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => loadInvitationList(currentPage + 1)}
                      >
                        下一页
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground">暂无邀请记录</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    分享您的邀请码给好友，开始邀请吧！
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
